import { Box } from '@mui/material';
import React, { useLayoutEffect } from 'react';
import { HowDoesItWorkModule } from '../how-does-it-work-module';
import { LearnMorePopout } from '../learn-more-popout';
import { ReachMoreModule } from '../reach-more-module';
import { RichResourcesModule } from '../rich-resources-module';
import { ReadyToEnrollModule } from '../ready-to-enroll-module';
import { WelcomeModule } from '../welcome-module';
import { WhatIsEsppModule } from '../what-is-espp-module';
import { useHomepage } from './hooks';
import { EmptyModuleWrapperWithoutExtraPadding } from '../homepage-module-wrapper/homepage-module-wrapper';
import { HomePageContext } from '../homepage-context';
import { LoadingOverlay } from '../../loading-overlay/loading-overlay';
import type { IModuleWithImageData } from '../../../../dotcms/dotcms-models/dotcms-module-with-image';
import { ErrorOccurredScreen } from '../../../root-error-boundary/error-occurred-screen';

const Homepage: React.FC = () => {
  const {
    theme,
    muiTheme,
    data,
    loading,
    error,
    defaultDocumentDescription,
    setDocumentDescription,
    isCashlessPageAvailable,
    planIsEsppOnly,
  } = useHomepage();

  useLayoutEffect(() => {
    if (data) {
      setDocumentDescription(
        data.pageDescription || defaultDocumentDescription,
      );
    }
  }, [data, setDocumentDescription, defaultDocumentDescription]);

  if (loading) {
    return <LoadingOverlay />;
  }

  if (error || !data) {
    return (
      <ErrorOccurredScreen
        errorReason={error || 'no data'}
        componentName="Homepage"
      />
    );
  }

  const {
    welcomeModule,
    welcomeModuleEsppOnly,
    learnMorePopout,
    whatIsEsppModule,
    whatIsEsppModuleEsppOnly,
    howDoesItWorkModule,
    howDoesItWorkModuleEsppOnly,
    reachMoreModule,
    richResourcesModule,
    richResourcesModuleEsppOnly,
    readyToEnrollModule,
  } = data;

  const style = {
    '&>:nth-of-type(odd)': {
      backgroundColor: theme.colors.eggshellBackground,
    },
    '&>:nth-of-type(even)': {
      backgroundColor: theme.colors.grayscale.backgroundGrey,
    },
    '&>:first-of-type': {
      paddingTop: '64px',
    },
  };

  const renderWhatIsEsppAndHowDoesItWorkModules = (
    whatIsEspp: IModuleWithImageData | undefined,
    howDoesItWork: IModuleWithImageData | undefined,
  ) => (
    <>
      {whatIsEspp && <WhatIsEsppModule data={whatIsEspp} />}
      {/* Added an empty model so as not to break background colors for modules
    because WhatIsEsppModule and HowDoesItWorkModule have the same color */}
      {whatIsEspp && howDoesItWork && <EmptyModuleWrapperWithoutExtraPadding />}
      {howDoesItWork && <HowDoesItWorkModule data={howDoesItWork} />}
    </>
  );

  return (
    <HomePageContext.Provider value={data}>
      <Box sx={style} data-component="Homepage">
        {planIsEsppOnly ? (
          <>
            {welcomeModuleEsppOnly && (
              <WelcomeModule data={welcomeModuleEsppOnly} />
            )}
            {renderWhatIsEsppAndHowDoesItWorkModules(
              whatIsEsppModuleEsppOnly,
              howDoesItWorkModuleEsppOnly,
            )}
            {richResourcesModuleEsppOnly && (
              <RichResourcesModule
                data={richResourcesModuleEsppOnly}
                muiTheme={muiTheme}
              />
            )}
          </>
        ) : (
          <>
            {welcomeModule && <WelcomeModule data={welcomeModule} />}
            {welcomeModule && learnMorePopout && isCashlessPageAvailable && (
              <LearnMorePopout
                theme={theme}
                muiTheme={muiTheme}
                prevBackgroundColor={theme.colors.eggshellBackground}
                nextBackgroundColor={theme.colors.grayscale.backgroundGrey}
                data={learnMorePopout}
              />
            )}
            {/* Added an empty model so as not to break background colors for modules
        because LearnMorePopout has no color */}
            {welcomeModule && learnMorePopout && isCashlessPageAvailable && (
              <div />
            )}
            {renderWhatIsEsppAndHowDoesItWorkModules(
              whatIsEsppModule,
              howDoesItWorkModule,
            )}
            {reachMoreModule && (
              <ReachMoreModule data={reachMoreModule} muiTheme={muiTheme} />
            )}
            {richResourcesModule && (
              <RichResourcesModule
                data={richResourcesModule}
                muiTheme={muiTheme}
              />
            )}
          </>
        )}
        {readyToEnrollModule && (
          <ReadyToEnrollModule data={readyToEnrollModule} />
        )}
      </Box>
    </HomePageContext.Provider>
  );
};

export default Homepage;
